// Generic client-side column sort for a ledger-style admin table. Extracted
// from admin/src/pages/AllProformasPage.jsx (its original SORT_COLUMNS /
// compareRows / handleSort trio) so AllRequestsPage, ApprovalQueuePage and
// QueuePage don't each reinvent it — see those pages' own header comments
// for which columns they wire up.
//
// `columns` is a map of { [columnId]: { key, numeric } } — `key` is the row
// field to read, `numeric` compares as Number instead of a lower-cased
// string. Null/undefined/empty values always sort last, independent of
// direction (a request with no priced-by, no valid_until, etc. reads as
// "nothing here yet", not as the smallest or largest value).
//
// This hook owns sortCol/sortDir state and the toggle-on-repeat-click
// behaviour (same column clicked again flips direction; a different column
// resets to 'asc'), but does NOT own filtering — callers filter first, then
// pass the filtered array to `sortRows`. `sortRows` never mutates its input
// (the array handed in is very often a TanStack Query cache result, or a
// `.filter()` output a caller still reads afterwards).

import { useCallback, useState } from 'react';

export function compareSortableRows(a, b, columns, sortCol, dir) {
  const { key, numeric } = columns[sortCol];
  let av = a[key];
  let bv = b[key];
  if (av === null || av === undefined || av === '') return 1;
  if (bv === null || bv === undefined || bv === '') return -1;
  if (numeric) {
    av = Number(av);
    bv = Number(bv);
  } else {
    av = String(av).toLowerCase();
    bv = String(bv).toLowerCase();
  }
  if (av < bv) return dir === 'asc' ? -1 : 1;
  if (av > bv) return dir === 'asc' ? 1 : -1;
  return 0;
}

export function useSortableList(columns, defaultCol, defaultDir = 'asc') {
  const [sortCol, setSortCol] = useState(defaultCol);
  const [sortDir, setSortDir] = useState(defaultDir);

  const handleSort = useCallback(
    (column) => {
      if (sortCol === column) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortCol(column);
        setSortDir('asc');
      }
    },
    [sortCol]
  );

  // Stable across renders where sortCol/sortDir haven't changed, so a
  // caller's own useMemo keyed on `sortRows` doesn't recompute on every
  // unrelated re-render (see AllProformasPage.jsx's own rows useMemo).
  const sortRows = useCallback(
    (rows) => [...rows].sort((a, b) => compareSortableRows(a, b, columns, sortCol, sortDir)),
    [columns, sortCol, sortDir]
  );

  return { sortCol, sortDir, handleSort, sortRows };
}

export default useSortableList;
